import React, { useState, useEffect } from 'react';
import {
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Button, 
  Stack,
  useTheme,
  useMediaQuery,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { 
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useWallet } from '../context/WalletContext';
import { themeCustom, GradientPaper, HeaderBar } from '../utils/themeUtils';
import { ThemeProvider } from '@mui/material/styles';
import { ethers } from 'ethers';

export default function Dashboard() {
  const navigate = useNavigate();
  const {
    provider,
    signer,
    address,
    isConnected,
    contract,
    currentNetwork,
    networkConfig,
    wallets,
    activeWallet,
    switchWallet,
    handleNetworkChange,
    connectWallet,
    disconnectWallet,
    isConnecting,
    connectionStatus,
    isInitialized,
    initError
  } = useWallet();

  const [lockedTokens, setLockedTokens] = useState([]);
  const [unlockedTokens, setUnlockedTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tokenCache, setTokenCache] = useState({}); // Cache for token details
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Instant navigation functions
  const navigateToLockedTokens = () => {
    // Store current data in sessionStorage for instant loading
    sessionStorage.setItem('dashboardLockedTokens', JSON.stringify(lockedTokens));
    sessionStorage.setItem('dashboardUnlockedTokens', JSON.stringify(unlockedTokens));
    sessionStorage.setItem('dashboardTokenCache', JSON.stringify(tokenCache));
    navigate('/locked-tokens');
  };

  const navigateToUnlockedTokens = () => {
    // Store current data in sessionStorage for instant loading
    sessionStorage.setItem('dashboardLockedTokens', JSON.stringify(lockedTokens));
    sessionStorage.setItem('dashboardUnlockedTokens', JSON.stringify(unlockedTokens));
    sessionStorage.setItem('dashboardTokenCache', JSON.stringify(tokenCache));
    navigate('/unlocked-tokens');
  };

  // Optimized token details fetching with caching
  const getTokenDetails = async (tokenAddress) => {
    // Check cache first
    if (tokenCache[tokenAddress]) {
      return tokenCache[tokenAddress];
    }

    try {
      const tokenContract = new ethers.Contract(
        tokenAddress, 
        ['function symbol() view returns (string)', 'function decimals() view returns (uint8)'], 
        provider
      );
      
      const [symbol, decimals] = await Promise.all([
        tokenContract.symbol(),
        tokenContract.decimals()
      ]);
      
      const tokenData = { symbol, decimals };
      
      // Cache the result
      setTokenCache(prev => ({ ...prev, [tokenAddress]: tokenData }));
      
      return tokenData;
    } catch (error) {
      console.error(`Error fetching token details for ${tokenAddress}:`, error);
      // Return fallback data
      return { 
        symbol: tokenAddress.slice(0, 6) + '...' + tokenAddress.slice(-4), 
        decimals: 18 
      };
    }
  };

  // Optimized fetch user's locked and unlocked tokens
  const fetchUserTokens = async () => {
    if (!isConnected || !contract || !address) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching user tokens for address:', address);
      
      // Get total number of locks
      const lockCount = await contract.lockCount();
      console.log(`Total locks in contract: ${lockCount.toString()}`);
      
      // Only fetch recent locks for better performance (last 50)
      const maxLocksToFetch = 50;
      const startIndex = Math.max(0, lockCount - maxLocksToFetch);
      const locksToFetch = Math.min(lockCount, maxLocksToFetch);
      
      console.log(`Fetching recent locks from ${startIndex} to ${startIndex + locksToFetch - 1}`);
      
      // Fetch locks in smaller batches for better performance
      const batchSize = 10;
      const allLocks = [];
      
      for (let batchStart = startIndex; batchStart < startIndex + locksToFetch; batchStart += batchSize) {
        const batchEnd = Math.min(batchStart + batchSize, startIndex + locksToFetch);
        const batchPromises = [];
        
        for (let i = batchStart; i < batchEnd; i++) {
          batchPromises.push(contract.locks(i));
        }
        
        const batchResults = await Promise.all(batchPromises);
        batchResults.forEach((lock, index) => {
          allLocks.push({ ...lock, lockId: batchStart + index });
        });
      }
      
      console.log(`Fetched ${allLocks.length} locks`);
      
      // Filter ONLY user's locks
      const userLocks = allLocks.filter(lock => 
        lock.owner.toLowerCase() === address.toLowerCase()
      );
      
      console.log(`Found ${userLocks.length} locks for user ${address}`);
      
      if (userLocks.length === 0) {
        setLockedTokens([]);
        setUnlockedTokens([]);
        setLoading(false);
        setIsInitialLoad(false);
        return;
      }
      
      // Get unique token addresses for this user only
      const uniqueTokenAddresses = [...new Set(userLocks.map(lock => lock.token))];
      console.log(`Unique tokens for user: ${uniqueTokenAddresses.length}`);
      
      // Fetch token details in parallel
      const tokenDetailsPromises = uniqueTokenAddresses.map(getTokenDetails);
      const tokenDetailsResults = await Promise.all(tokenDetailsPromises);
      
      // Create token details map
      const tokenDetailsMap = {};
      uniqueTokenAddresses.forEach((tokenAddress, index) => {
        tokenDetailsMap[tokenAddress] = tokenDetailsResults[index];
      });
      
      // Process user's locks
      const userLockedTokens = [];
      const userUnlockedTokens = [];
      
      userLocks.forEach(lock => {
        const tokenDetails = tokenDetailsMap[lock.token];
        const formattedAmount = ethers.utils.formatUnits(lock.amount, tokenDetails.decimals);
        const unlockTime = new Date(lock.unlockTime * 1000);
        const isUnlockable = Date.now() >= unlockTime.getTime();
        
        const tokenData = {
          lockId: lock.lockId,
          tokenAddress: lock.token,
          symbol: tokenDetails.symbol,
          decimals: tokenDetails.decimals,
          amount: lock.amount,
          formattedAmount,
          unlockTime,
          isUnlockable,
          withdrawn: lock.withdrawn
        };
        
        if (lock.withdrawn) {
          userUnlockedTokens.push(tokenData);
        } else {
          userLockedTokens.push(tokenData);
        }
      });
      
      // Sort by lock ID (most recent first)
      userLockedTokens.sort((a, b) => b.lockId - a.lockId);
      userUnlockedTokens.sort((a, b) => b.lockId - a.lockId);
      
      console.log(`User has ${userLockedTokens.length} locked and ${userUnlockedTokens.length} unlocked tokens`);
      
      setLockedTokens(userLockedTokens);
      setUnlockedTokens(userUnlockedTokens);
      
    } catch (error) {
      console.error('Error fetching user tokens:', error);
      setError('Failed to fetch tokens. Please try again.');
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  // Unlock tokens function
  const unlockTokens = async (lockId) => {
    if (!contract || !signer) return;
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('Unlocking tokens for lock ID:', lockId);
      
      const unlockTx = await contract.unlockTokens(lockId);
      console.log('Unlock transaction sent:', unlockTx.hash);
      
      await unlockTx.wait();
      console.log('Tokens unlocked successfully!');
      
      // Refresh the data immediately
      await fetchUserTokens();
      
    } catch (error) {
      console.error('Error unlocking tokens:', error);
      if (error.message.includes('user rejected')) {
        setError('Transaction was rejected. Please try again.');
      } else {
        setError(error.message || 'Failed to unlock tokens. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch tokens when connected
  useEffect(() => {
    if (isConnected && contract && address) {
      fetchUserTokens();
    }
  }, [isConnected, contract, address, currentNetwork]);

  // Clear data when disconnected
  useEffect(() => {
    if (!isConnected) {
      setLockedTokens([]);
      setUnlockedTokens([]);
    }
  }, [isConnected]);

  // Manual refresh
  const handleRefresh = () => {
    fetchUserTokens();
  };

  // Format time remaining
  const formatTimeRemaining = (unlockTime) => {
    const now = Date.now();
    const timeLeft = unlockTime.getTime() - now;
    
    if (timeLeft <= 0) return 'Ready to unlock';
    
    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) return `${days}d ${hours}h remaining`;
    if (hours > 0) return `${hours}h ${minutes}m remaining`;
    return `${minutes}m remaining`;
  };

  // Format unlock time
  const formatUnlockTime = (unlockTime) => {
    return unlockTime.toLocaleDateString() + ' ' + unlockTime.toLocaleTimeString();
  };

  return (
    <ThemeProvider theme={themeCustom}>
      <Box sx={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #000000 0%, #141414 50%, #1a1a2e 100%)',
        py: isMobile ? 2 : 6,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(0, 113, 235, 0.1) 0%, transparent 50%), 
            radial-gradient(circle at 80% 20%, rgba(0, 86, 179, 0.1) 0%, transparent 50%)
          `,
          pointerEvents: 'none',
        }
      }}>
        <Container maxWidth="lg" sx={{ px: isMobile ? 0.5 : 2, position: 'relative', zIndex: 1 }}>
          <HeaderBar>
            <LockIcon sx={{ 
              fontSize: isMobile ? 32 : 64, 
              mr: isMobile ? 1 : 2,
              filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))'
            }} />
            <Typography 
              variant={isMobile ? 'h6' : 'h4'} 
              fontWeight={700} 
              letterSpacing={1} 
              sx={{ 
                color: '#fff', 
                textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5))'
              }}
            >
              Dashboard
            </Typography>
          </HeaderBar>

          <Stack spacing={isMobile ? 2 : 3}>
            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Network Info */}
            <GradientPaper>
              <Typography 
                variant={isMobile ? 'body1' : 'h6'} 
                gutterBottom 
                sx={{ 
                  mb: isMobile ? 2 : 3,
                  color: '#0071EB',
                  fontWeight: 700
                }}
              >
                Network: {networkConfig?.name || 'Unknown'}
              </Typography>
              <Typography variant="body2" color="#B3B3B3">
                Connected: {isConnected ? `${address?.slice(0, 6)}...${address?.slice(-4)}` : 'Not connected'}
              </Typography>
            </GradientPaper>

            {/* Quick Actions */}
            <GradientPaper>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 3 
              }}>
                <Typography 
                  variant={isMobile ? 'body1' : 'h6'} 
                  sx={{ 
                    color: '#0071EB',
                    fontWeight: 700,
                    background: 'linear-gradient(90deg, #0071EB 0%, #FFA500 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  Quick Actions
                </Typography>
                <Chip 
                  label="Main Features" 
                  size="small" 
                  sx={{ 
                    background: 'rgba(0, 113, 235, 0.1)',
                    color: '#0071EB',
                    border: '1px solid rgba(0, 113, 235, 0.3)'
                  }} 
                />
              </Box>
              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12} sm={6} md={4}>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      onClick={() => navigate('/lock-tokens')}
                      variant="contained"
                      startIcon={<AddIcon />}
                      fullWidth
                      sx={{
                        background: 'linear-gradient(135deg, #0071EB 0%, #0056B3 100%)',
                        color: '#FFFFFF',
                        fontWeight: 700,
                        py: isMobile ? 2 : 2.5,
                        fontSize: isMobile ? '14px' : '16px',
                        borderRadius: 3,
                        boxShadow: '0 4px 20px rgba(0, 113, 235, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #0056B3 0%, #003D82 100%)',
                          boxShadow: '0 8px 30px rgba(0, 113, 235, 0.4)',
                        }
                      }}
                    >
                      🔒 Lock New Tokens
                    </Button>
                  </motion.div>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      onClick={navigateToLockedTokens}
                      variant="outlined"
                      startIcon={<LockIcon />}
                      fullWidth
                      sx={{
                        borderColor: '#0071EB',
                        color: '#0071EB',
                        fontWeight: 700,
                        py: isMobile ? 2 : 2.5,
                        fontSize: isMobile ? '14px' : '16px',
                        borderRadius: 3,
                        borderWidth: '2px',
                        '&:hover': {
                          borderColor: '#0056B3',
                          backgroundColor: 'rgba(0, 113, 235, 0.1)',
                          borderWidth: '2px',
                        }
                      }}
                    >
                      💎 My Locked Tokens
                    </Button>
                  </motion.div>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      onClick={navigateToUnlockedTokens}
                      variant="outlined"
                      startIcon={<LockOpenIcon />}
                      fullWidth
                      sx={{
                        borderColor: '#FFA500',
                        color: '#FFA500',
                        fontWeight: 700,
                        py: isMobile ? 2 : 2.5,
                        fontSize: isMobile ? '14px' : '16px',
                        borderRadius: 3,
                        borderWidth: '2px',
                        '&:hover': {
                          borderColor: '#FF8C00',
                          backgroundColor: 'rgba(255, 165, 0, 0.1)',
                          borderWidth: '2px',
                        }
                      }}
                    >
                      📈 Unlock History
                    </Button>
                  </motion.div>
                </Grid>
              </Grid>
            </GradientPaper>

            {/* Locked Tokens Summary */}
            <GradientPaper>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'flex-start' : 'center', 
                mb: 3,
                gap: isMobile ? 1 : 0
              }}>
                <Typography 
                  variant={isMobile ? 'body1' : 'h6'} 
                  sx={{ 
                    color: '#0071EB',
                    fontWeight: 700
                  }}
                >
                  Your Locked Tokens ({lockedTokens.length})
                </Typography>
                <Button
                  onClick={handleRefresh}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={16} /> : null}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{ 
                    color: '#0071EB',
                    fontSize: isMobile ? '12px' : '14px'
                  }}
                >
                  {loading ? 'Loading...' : 'Refresh'}
                </Button>
              </Box>
              
              {isInitialLoad ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={32} sx={{ color: '#0071EB', mb: 2 }} />
                  <Typography color="#B3B3B3" variant={isMobile ? 'body2' : 'body1'}>
                    Loading your tokens...
                  </Typography>
                </Box>
              ) : loading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={24} sx={{ color: '#0071EB' }} />
                  <Typography color="#B3B3B3" sx={{ mt: 1 }} variant={isMobile ? 'body2' : 'body1'}>
                    Refreshing...
                  </Typography>
                </Box>
              ) : lockedTokens.length === 0 ? (
                <Typography color="#B3B3B3" sx={{ textAlign: 'center', py: 4 }} variant={isMobile ? 'body2' : 'body1'}>
                  No locked tokens found. Lock some tokens to get started!
                </Typography>
              ) : (
                <Box sx={{ overflow: 'auto' }}>
                  {isMobile ? (
                    // Mobile card layout
                    <Stack spacing={2}>
                      {lockedTokens.map((token) => (
                        <Card key={token.lockId} sx={{ 
                          background: 'linear-gradient(135deg, #141414 0%, rgba(20, 20, 20, 0.9) 100%)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: 2
                        }}>
                          <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="h6" sx={{ color: '#0071EB', fontWeight: 700 }}>
                                {token.symbol}
                              </Typography>
                              <Chip
                                label={token.isUnlockable ? 'Ready to unlock' : formatTimeRemaining(token.unlockTime)}
                                color={token.isUnlockable ? 'success' : 'warning'}
                                size="small"
                              />
                            </Box>
                            <Typography variant="body2" color="#B3B3B3" sx={{ mb: 1 }}>
                              Amount: {token.formattedAmount}
                            </Typography>
                            <Typography variant="body2" color="#B3B3B3" sx={{ mb: 2 }}>
                              Unlock: {formatUnlockTime(token.unlockTime)}
                            </Typography>
                            {token.isUnlockable ? (
                              <Button
                                variant="contained"
                                fullWidth
                                onClick={() => unlockTokens(token.lockId)}
                                disabled={loading}
                                sx={{
                                  background: 'linear-gradient(135deg, #0071EB 0%, #0056B3 100%)',
                                  color: '#FFFFFF',
                                  fontWeight: 600,
                                  fontSize: '14px',
                                  '&:hover': {
                                    background: 'linear-gradient(135deg, #0056B3 0%, #003D82 100%)',
                                  },
                                  '&:disabled': {
                                    background: 'rgba(128, 128, 128, 0.3)',
                                  }
                                }}
                              >
                                {loading ? <CircularProgress size={16} /> : 'Unlock'}
                              </Button>
                            ) : (
                              <Typography variant="body2" color="#B3B3B3" sx={{ textAlign: 'center' }}>
                                Locked
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  ) : (
                    // Desktop table layout
                    <TableContainer component={Paper} sx={{ maxHeight: 400, overflow: 'auto' }}>
                      <Table size="medium">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, color: '#0071EB' }}>Token</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#0071EB' }}>Amount</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#0071EB' }}>Unlock Time</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#0071EB' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#0071EB' }}>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {lockedTokens.map((token) => (
                            <TableRow key={token.lockId} hover>
                              <TableCell>{token.symbol}</TableCell>
                              <TableCell>{token.formattedAmount}</TableCell>
                              <TableCell>{formatUnlockTime(token.unlockTime)}</TableCell>
                              <TableCell>
                                <Chip
                                  label={token.isUnlockable ? 'Ready to unlock' : formatTimeRemaining(token.unlockTime)}
                                  color={token.isUnlockable ? 'success' : 'warning'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {token.isUnlockable ? (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => unlockTokens(token.lockId)}
                                    disabled={loading}
                                    sx={{
                                      background: 'linear-gradient(135deg, #0071EB 0%, #0056B3 100%)',
                                      color: '#FFFFFF',
                                      fontWeight: 600,
                                      '&:hover': {
                                        background: 'linear-gradient(135deg, #0056B3 0%, #003D82 100%)',
                                      },
                                      '&:disabled': {
                                        background: 'rgba(128, 128, 128, 0.3)',
                                      }
                                    }}
                                  >
                                    {loading ? <CircularProgress size={16} /> : 'Unlock'}
                                  </Button>
                                ) : (
                                  <Typography variant="body2" color="#B3B3B3">
                                    Locked
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}
            </GradientPaper>

            {/* Unlocked Tokens Summary */}
            <GradientPaper>
              <Typography 
                variant={isMobile ? 'body1' : 'h6'} 
                gutterBottom 
                sx={{ 
                  mb: isMobile ? 2 : 3,
                  color: '#0071EB',
                  fontWeight: 700
                }}
              >
                Your Unlocked History ({unlockedTokens.length})
              </Typography>
              
              {isInitialLoad ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={32} sx={{ color: '#0071EB', mb: 2 }} />
                  <Typography color="#B3B3B3" variant={isMobile ? 'body2' : 'body1'}>
                    Loading your history...
                  </Typography>
                </Box>
              ) : loading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={24} sx={{ color: '#0071EB' }} />
                  <Typography color="#B3B3B3" sx={{ mt: 1 }} variant={isMobile ? 'body2' : 'body1'}>
                    Refreshing...
                  </Typography>
                </Box>
              ) : unlockedTokens.length === 0 ? (
                <Typography color="#B3B3B3" sx={{ textAlign: 'center', py: 4 }} variant={isMobile ? 'body2' : 'body1'}>
                  No unlocked tokens found.
                </Typography>
              ) : (
                <Box sx={{ overflow: 'auto' }}>
                  {isMobile ? (
                    // Mobile card layout
                    <Stack spacing={2}>
                      {unlockedTokens.slice(0, 5).map((token) => (
                        <Card key={token.lockId} sx={{ 
                          background: 'linear-gradient(135deg, #141414 0%, rgba(20, 20, 20, 0.9) 100%)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: 2
                        }}>
                          <CardContent sx={{ p: 2 }}>
                            <Typography variant="h6" sx={{ color: '#0071EB', fontWeight: 700, mb: 1 }}>
                              {token.symbol}
                            </Typography>
                            <Typography variant="body2" color="#B3B3B3" sx={{ mb: 1 }}>
                              Amount: {token.formattedAmount}
                            </Typography>
                            <Typography variant="body2" color="#B3B3B3">
                              Unlocked: {formatUnlockTime(token.unlockTime)}
                            </Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  ) : (
                    // Desktop table layout
                    <TableContainer component={Paper} sx={{ maxHeight: 300, overflow: 'auto' }}>
                      <Table size="medium">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, color: '#0071EB' }}>Token</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#0071EB' }}>Amount</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#0071EB' }}>Unlocked At</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {unlockedTokens.slice(0, 5).map((token) => (
                            <TableRow key={token.lockId} hover>
                              <TableCell>{token.symbol}</TableCell>
                              <TableCell>{token.formattedAmount}</TableCell>
                              <TableCell>{formatUnlockTime(token.unlockTime)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}
            </GradientPaper>
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
} 